/**
 * Centralized API Client
 *
 * Axios instance with request/response interceptors for:
 * - Automatic auth token injection
 * - Mobile app header detection
 * - Centralized 401 handling (auto-logout)
 */

import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../stores/auth.store';
import { useUpgradePromptStore } from '../stores/upgrade-prompt.store';
import { getEchoIfExists, isWsEnabled } from './echo';
import {
  isInsufficientCreditsError,
  isEntitlementRequiredError,
  isCircleAgreementRequiredError,
  isEmpathyNormsRequiredError,
  isAccountBannedError,
} from '../types/errors';
import { useCircleAgreementStore } from '../stores/circle-agreement.store';
import { useEmpathyNormsStore } from '../stores/empathy-norms.store';
import { useAccountBanStore } from '../stores/account-ban.store';
import { isCurrentSession, issueRead, type SessionRead } from '../stores/session-read';
import { hardNavigate } from '../utils/hard-navigate';
import { loginUrl } from '../utils/safe-redirect';

const API_URL = import.meta.env.VITE_API_URL;

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Send this request under THIS bearer token instead of the one signed in
     * now. Only for the work an ending session does on its own way out (the
     * empathy queue's leave and hangup on a sign-out or a switch): the session
     * change has already replaced the token by the time those requests reach
     * this interceptor, which would otherwise send them as the NEXT account,
     * or with no token at all. The request is bound to that session, so its
     * answer (a 401 included) never reacts for whoever is signed in now.
     */
    sessionToken?: string | null;
  }
}

/** A request config carrying the session ticket it was sent under. */
type SessionBoundConfig = InternalAxiosRequestConfig & { _sessionRead?: SessionRead };

/**
 * Axios instance configured with base URL and default headers
 */
export const api = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor
 * - Injects Authorization header from auth store
 * - Adds mobile app identifiers for Capacitor
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Inject auth token: the pinned session's, if the caller named one.
    const pinned = config.sessionToken !== undefined;
    const token = pinned ? config.sessionToken : useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (pinned) {
      // A pinned request never borrows a header from anywhere else.
      delete config.headers.Authorization;
    }
    // Bind the request to the session it goes out under (session-read.ts), so
    // the response handler below reacts only to answers for THAT session.
    (config as SessionBoundConfig)._sessionRead = pinned ? issueRead(token ?? null) : issueRead();

    // Add mobile app identifiers if running in Capacitor
    if (Capacitor.isNativePlatform()) {
      config.headers['X-App-Type'] = 'nvc-mobile-app';
      config.headers['User-Agent'] = `NVCApp/${Capacitor.getPlatform()} Capacitor`;
    }

    // Add WebSocket socket ID so Reverb's ->toOthers() can exclude the sender.
    // Only read from an existing Echo instance — don't eagerly create one on
    // unauthenticated requests (like login).
    if (isWsEnabled()) {
      try {
        const socketId = getEchoIfExists()?.socketId();
        if (socketId) {
          config.headers['X-Socket-Id'] = socketId;
        }
      } catch {
        /* Echo not initialized yet */
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * An error this interceptor already showed to the user (the global
 * UpgradePrompt for a structured 402 / 403). A caller reads the mark with
 * isGloballyHandled() and raises no dialog or toast of its own for it, so the
 * user sees one answer to one refusal (review round 1 of #1054). Callers learn
 * it from the error, never by peeking at the prompt's store.
 */
export type GloballyHandledError = (AxiosError | StreamRefusalError) & { globallyHandled: true };

export function isGloballyHandled(error: unknown): error is GloballyHandledError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { globallyHandled?: unknown }).globallyHandled === true
  );
}

/**
 * The one answer to a structured refusal, for every client that can get one:
 * a 402 insufficient_credits opens the global UpgradePrompt in its credits
 * mode (the store fires the `out_of_credits` event, once per open, with the
 * server's `details.feature` through gaCreditFeature), a 403
 * entitlement_required in its plan mode. True when it answered. The axios
 * interceptor below and streamRefusal() (the fetch-based stream readers) both
 * decide here, so the two cannot drift.
 */
function raiseGlobalRefusal(status: number | undefined, payload: unknown): boolean {
  if (status === 402 && isInsufficientCreditsError(payload)) {
    useUpgradePromptStore.getState().show('credits', payload.details);
    return true;
  }
  if (status === 403 && isEntitlementRequiredError(payload)) {
    useUpgradePromptStore.getState().show('plan', payload.details);
    return true;
  }
  return false;
}

/**
 * A refusal a `fetch` stream got (Empy's text, the support chat), already
 * answered by the global UpgradePrompt. isGloballyHandled() reads it like the
 * interceptor's mark, and `response.data` carries the server's body as on an
 * AxiosError, so a caller handles both the same way: nothing of its own.
 */
export class StreamRefusalError extends Error {
  readonly globallyHandled = true as const;
  readonly response: { status: number; data: unknown };

  constructor(status: number, data: unknown) {
    const message = (data as { message?: unknown } | null)?.message;
    super(typeof message === 'string' && message ? message : `Refused with ${status}`);
    this.name = 'StreamRefusalError';
    this.response = { status, data };
  }
}

/**
 * For a request sent with `fetch`, which this file's interceptor never sees:
 * if the response is a structured 402 / 403 the global prompt answers, raise
 * it (raiseGlobalRefusal) and return the error for the caller to throw or
 * branch on; otherwise null, and the caller's own error path runs. Reads a
 * clone, so the caller can still read the body. `sentUnder` is the session
 * ticket taken before the fetch: an answer for a session that has ended by
 * the time its body is read raises nothing (#940), as in the interceptor.
 */
export async function streamRefusal(
  response: Response,
  sentUnder: SessionRead,
): Promise<StreamRefusalError | null> {
  let payload: unknown;
  try {
    payload = await response.clone().json();
  } catch {
    return null;
  }
  if (!isCurrentSession(sentUnder)) return null;
  if (!raiseGlobalRefusal(response.status, payload)) return null;
  return new StreamRefusalError(response.status, payload);
}

/** What a send resolves to when the global UpgradePrompt answered its refusal. */
export const REFUSAL_PROMPTED = 'refusal_prompted' as const;

/**
 * Response interceptor
 * - Handles 401 Unauthorized by logging out and redirecting
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Every reaction below is about the account that sent the request: signing
    // it out, suspending it, offering it an upgrade, parking its circle join.
    // An answer for a session that has since ended (a sign-out, or a sign-in as
    // someone else) must not land on whoever is signed in now (#940): a late
    // 401 for A's revoked token used to sign B out.
    const sentUnder = (error.config as SessionBoundConfig | undefined)?._sessionRead;
    if (sentUnder && !isCurrentSession(sentUnder)) return Promise.reject(error);

    if (error.response?.status === 401) {
      const authStore = useAuthStore.getState();

      // Only logout if currently authenticated (avoid loops)
      if (authStore.isAuthenticated) {
        // Clear auth state
        authStore.setAuthState({
          isAuthenticated: false,
          isLoading: false,
          token: null,
          user: null,
        });

        // Redirect to login, a full navigation to reset everything. The
        // sign-in continues to the page the 401 interrupted (a warm push tap
        // whose token was revoked, any page): backlog c-079.
        const { pathname, search, hash } = window.location;
        hardNavigate(loginUrl(`${pathname}${search}${hash}`, { session_expired: 'true' }));
      }
    }

    // 402 / 403 with structured error_code → open the global UpgradePrompt
    // modal. The backend exception classes (PR2) emit a uniform
    // RFC 7807-style payload; the store + modal pair handle mode-specific
    // copy. Errors without our error_code (e.g. generic 403 from auth)
    // fall through unchanged.
    const status = error.response?.status;
    const payload = error.response?.data;
    if (raiseGlobalRefusal(status, payload)) {
      (error as GloballyHandledError).globallyHandled = true;
    } else if (status === 403 && isAccountBannedError(payload)) {
      // Banned: every authenticated route answers this and the server has
      // revoked the token. Log out and raise the suspended screen.
      useAccountBanStore.getState().suspend(payload, sentUnder ?? issueRead());
    }

    // 403 circle_agreement_required → the user has never accepted (or hasn't
    // accepted the CURRENT version of) the confidentiality agreement, so the
    // server refused the session join. Park the request, raise the agreement, and
    // replay it if they accept — the caller just sees a successful join, and every
    // gated route is covered without touching a single join button. Joining a
    // circle happens on route mount from several entry points, so a per-call-site
    // check would be forgotten the first time a new one is added.
    //
    // Replaying is safe: the middleware rejected the request before the
    // controller, so nothing happened server-side.
    const config = error.config as
      | (InternalAxiosRequestConfig & { _agreementRetry?: boolean; _normsRetry?: boolean })
      | undefined;
    if (
      status === 403 &&
      isCircleAgreementRequiredError(payload) &&
      config &&
      !config._agreementRetry
    ) {
      const accepted = await useCircleAgreementStore.getState().request();
      // Replaying re-stamps the CURRENT token: if the session changed while the
      // sheet was up, the join would go out as someone else.
      if (accepted && (!sentUnder || isCurrentSession(sentUnder))) {
        config._agreementRetry = true; // one replay only; never loop
        return api.request(config);
      }
    }

    // 403 empathy_norms_required → "How empathy calls work" (P5, X2), same
    // park-ask-replay as the agreement above, with its own one-replay flag. When
    // a user owes BOTH, the server answers the agreement first (middleware
    // order); the agreement's replay then lands HERE, so the two sheets come up
    // one after the other and never together.
    if (status === 403 && isEmpathyNormsRequiredError(payload) && config && !config._normsRetry) {
      const accepted = await useEmpathyNormsStore.getState().request();
      if (accepted && (!sentUnder || isCurrentSession(sentUnder))) {
        config._normsRetry = true; // one replay only; never loop
        return api.request(config);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
